import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  BookText,
  Copy,
  LoaderCircle,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { useThemeMode } from "@/contexts/ThemeContext";
import { llmApi } from "@/services/rag-service";

import { useAuth } from "../../contexts/AuthContext";
import Configurations from "./Configurations";
import ProviderIcon from "./ProviderIcon";
import Snippet from "./Snippet";
import { ModelTypeBadge, ModelVisibilityBadge } from "./components/ModelBadges";
import useCopyToClipboard from "./hooks/useCopyToClipboard";
import type { LLMModel } from "./types";

type Props = {
  selectedModelId?: string;
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  handleOpenModal: (id: string | null, isPublic: boolean) => void;
  handleOpenDeleteModal: (model: LLMModel) => void;
};

const AICatalogDetailComp: React.FC<Props> = ({
  selectedModelId,
  open,
  onClose,
  isAdmin = false,
  handleOpenModal,
  handleOpenDeleteModal,
}) => {
  const { cache } = useAuth();
  const { mode } = useThemeMode();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [topHeight, setTopHeight] = useState(400);
  const [model, setModel] = useState<LLMModel | null>(null);
  const [loading, setLoading] = useState(true);
  const copyToClipboard = useCopyToClipboard();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const snippetScrollRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const hasFetched = useRef(false);
  const canManageModel = Boolean(
    model && (isAdmin || model.user_id === cache?.id),
  );

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;

    const containerTop = containerRef.current.getBoundingClientRect().top;
    setTopHeight(Math.max(150, event.clientY - containerTop));
  }, []);

  const getLLMConfig = useCallback(async () => {
    if (!selectedModelId) return;

    try {
      setLoading(true);
      setModel(null);
      const response = (await llmApi.getLLMConfig(
        selectedModelId,
      )) as unknown as LLMModel;
      setModel(response);
    } catch (error) {
      console.error(error);
      toast.error(t("aiCatalog.failedToLoadModel"));
    } finally {
      setLoading(false);
      hasFetched.current = false;
    }
  }, [selectedModelId, t]);

  useEffect(() => {
    if (selectedModelId && !hasFetched.current) {
      hasFetched.current = true;
      void getLLMConfig();
    }
  }, [selectedModelId, getLLMConfig]);

  useEffect(() => {
    if (open && model && snippetScrollRef.current) {
      snippetScrollRef.current.scrollTop = 0;
      closeButtonRef.current?.focus({ preventScroll: true });
    }
  }, [open, model]);

  useEffect(() => {
    const handleMouseUp = () => {
      isDragging.current = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    onClose();
    window.setTimeout(() => returnFocusRef.current?.focus(), 0);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side={isRTL ? "left" : "right"}
        dir={isRTL ? "rtl" : "ltr"}
        showCloseButton={false}
        overlayClassName="top-[60px]"
        onOpenAutoFocus={(event) => {
          if (document.activeElement instanceof HTMLElement) {
            returnFocusRef.current = document.activeElement;
          }
          event.preventDefault();
          closeButtonRef.current?.focus({ preventScroll: true });
        }}
        data-testid="ai-catalog-detail-sheet"
        className="top-[60px] h-[calc(100dvh-60px)] w-full max-w-full gap-0 bg-card p-0 shadow-lg sm:w-[500px] sm:max-w-[500px]"
      >
        <SheetTitle className="sr-only">
          {t("deploymentDetail.modelDetails")}
        </SheetTitle>
        <SheetDescription className="sr-only">
          {t("deploymentDetail.modelDetails")}
        </SheetDescription>

        {loading ? (
          <div
            role="status"
            className="flex h-full items-center justify-center"
          >
            <LoaderCircle className="size-6 animate-spin text-muted-foreground" />
            <span className="sr-only">{t("common.loading")}</span>
          </div>
        ) : !model ? (
          <div className="flex h-full items-center justify-center text-sm font-medium text-foreground">
            {t("common.noDataAvailable")}
          </div>
        ) : (
          <div ref={containerRef} className="flex h-full min-h-0 flex-col">
            <div
              style={{ height: topHeight }}
              className="min-h-[150px] shrink-0 overflow-y-auto"
            >
              <header className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <p className="flex-1 text-xs font-normal text-muted-foreground">
                    {t("deploymentDetail.modelDetails")}
                  </p>
                  <SheetClose asChild>
                    <Button
                      ref={closeButtonRef}
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t("common.close")}
                      data-testid="ai-catalog-detail-close-button"
                      className="size-6 text-muted-foreground"
                    >
                      <X className="size-4" />
                    </Button>
                  </SheetClose>
                </div>

                <div className="mt-2 flex min-h-[60px] items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="relative shrink-0">
                      <ProviderIcon provider={model.provider} />
                      <ModelVisibilityBadge model={model} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {model.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {model.provider}
                      </p>
                    </div>
                  </div>

                  {canManageModel && (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon-sm"
                        aria-label={t("common.edit")}
                        data-testid={`ai-catalog-detail-edit-model-${model.id}`}
                        onClick={() => {
                          onClose();
                          handleOpenModal(model.id, Boolean(model.is_public));
                        }}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon-sm"
                        aria-label={t("common.delete")}
                        data-testid={`ai-catalog-detail-delete-model-${model.id}`}
                        onClick={() => handleOpenDeleteModal(model)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </header>

              <div className="flex flex-col gap-4 p-3">
                {model.endpoint && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      {t("collections.endpointUrl")}
                    </p>
                    <div
                      data-testid="ai-catalog-detail-endpoint-field"
                      className="flex h-9 items-center gap-2 rounded-lg border border-input bg-input px-3 shadow-xs"
                    >
                      <span
                        data-testid="ai-catalog-detail-endpoint-input"
                        className="min-w-0 flex-1 truncate text-sm text-foreground"
                      >
                        {model.endpoint}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t("common.copy")}
                        data-testid={`ai-catalog-detail-copy-endpoint-${model.id}`}
                        className="size-5 shrink-0 text-muted-foreground"
                        onClick={() => copyToClipboard(model.endpoint!)}
                      >
                        <Copy className="size-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="self-start">
                  <ModelTypeBadge model={model} t={t} />
                </div>
                <Configurations model={model} t={t} />
              </div>
            </div>

            <div
              role="separator"
              aria-orientation="horizontal"
              onMouseDown={() => {
                isDragging.current = true;
              }}
              className="relative h-4 shrink-0 cursor-row-resize border-y border-border"
            >
              <span className="absolute left-1/2 top-1/2 grid h-4 w-3 -translate-x-1/2 -translate-y-1/2 place-items-center rounded bg-border text-[8px] text-muted-foreground">
                ⠿
              </span>
            </div>

            <div
              ref={snippetScrollRef}
              data-testid="ai-catalog-detail-snippet-scroll"
              className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden"
            >
              <Snippet
                model={model}
                t={t}
                mode={mode as "light" | "dark"}
              />
            </div>

            <div
              data-testid="ai-catalog-detail-documentation"
              className="flex h-10 shrink-0 items-center gap-2 px-3 text-sm text-muted-foreground"
            >
              <BookText className="size-4" />
              <span className="flex-1">{t("aiCatalog.documentation")}</span>
              <ArrowUpRight className="size-4 rtl:-scale-x-100" />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AICatalogDetailComp;
